import React, { useEffect, useMemo, useState } from "react";
import { KeyRound, Shield, Search, Save, Info } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero, Section, Badge, Button, Input, Field, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const DIMENSIONS = [
  { key: "module",   label: "Modules & pages",       help: "Which pages the role can open" },
  { key: "data",     label: "Data access",           help: "Read/write scopes on business data" },
  { key: "widget",   label: "Dashboard widgets",     help: "Which widgets appear on the dashboard" },
  { key: "activity", label: "Activity feed",         help: "Which categories appear in Recent Activity" },
  { key: "search",   label: "Search scopes",         help: "Which collections a global search can hit" },
  { key: "report",   label: "Reports",               help: "Which report categories may be generated" },
  { key: "notif",    label: "Notifications",         help: "Which categories the user may receive" },
  { key: "admin",    label: "Administrative actions", help: "Elevated operations" },
];

function hasPerm(role, key) {
  const grants = role?.permissions || [];
  if (grants.includes(key)) return true;
  if (grants.includes("*:*")) return true;
  if (grants.includes(`${key.split(":")[0]}:*`)) return true;
  const parts = key.split(":");
  if (parts.length === 3 && grants.includes(`${parts[0]}:${parts[1]}:*`)) return true;
  return false;
}

export default function PermissionManagement() {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // draft permissions for the active role

  async function load() {
    const [c, r] = await Promise.all([
      api.get("/permissions/catalog"),
      api.get("/permissions/roles"),
    ]);
    setCatalog(c.data || []);
    setRoles(r.data || []);
    if (!activeKey && r.data?.length) setActiveKey(r.data[0].key);
  }
  useEffect(() => { load().catch(() => toast.error("Failed to load permissions")); /* eslint-disable-next-line */ }, []);

  const active = roles.find(r => r.key === activeKey);
  useEffect(() => { if (active) setPending(new Set(active.permissions || [])); }, [active?.key]);// eslint-disable-line

  const groupedCatalog = useMemo(() => {
    const g = {};
    for (const p of catalog) {
      if (q && !`${p.key} ${p.label}`.toLowerCase().includes(q.toLowerCase())) continue;
      (g[p.dimension] = g[p.dimension] || []).push(p);
    }
    return g;
  }, [catalog, q]);

  function toggle(key) {
    if (!pending) return;
    const next = new Set(pending);
    if (next.has(key)) next.delete(key); else next.add(key);
    setPending(next);
  }

  async function save() {
    if (!active || !pending) return;
    setBusy(true);
    try {
      await api.patch(`/permissions/roles/${active.key}`, { permissions: Array.from(pending) });
      toast.success(`Permissions updated for ${active.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  }

  const dirty = pending && active && (
    pending.size !== (active.permissions || []).length ||
    (active.permissions || []).some(p => !pending.has(p))
  );

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <PageHero
        eyebrow="Administration"
        title="Permission management"
        subtitle="Grant, revoke and audit every role's access. Changes take effect within 15 seconds — no restart required."
        moduleKind="dashboard"
        actions={
          <Button
            variant="primary"
            disabled={!dirty || busy}
            onClick={save}
            data-testid="permissions-save-btn"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <Section title="Roles" description="System + custom roles.">
          <ul className="space-y-1" data-testid="role-list">
            {roles.map(r => (
              <li key={r.key}>
                <button
                  onClick={() => setActiveKey(r.key)}
                  data-testid={`role-item-${r.key}`}
                  className={`w-full text-left rounded-xl px-3 py-2.5 border transition-colors ${
                    activeKey === r.key ? "border-primary/40 bg-primary/5" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-lg bg-primary/10 text-primary grid place-items-center"><Shield className="w-3.5 h-3.5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{r.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{r.key}</div>
                    </div>
                    {r.is_executive && <Badge variant="primary">Exec</Badge>}
                    {r.is_system ? <Badge variant="neutral">System</Badge> : <Badge variant="info">Custom</Badge>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Section>

        <Section className="relative overflow-hidden">
          <Pattern kind="dashboard" />
          <div className="relative">
            {!active ? (
              <EmptyState icon={KeyRound} title="Select a role" description="Pick a role from the list to view and edit its permissions." />
            ) : (
              <>
                <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                  <div>
                    <div className="eyebrow">Editing</div>
                    <h2 className="font-heading text-2xl font-bold tracking-tight">{active.name}</h2>
                    <p className="subtle mt-1 max-w-2xl">{active.description || "—"}</p>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {active.is_system && <Badge variant="neutral">System role</Badge>}
                      {active.is_executive && <Badge variant="primary">Executive visibility</Badge>}
                      <Badge variant="info" data-testid="role-active-perm-count">{pending?.size ?? 0} permissions granted</Badge>
                    </div>
                  </div>
                  <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter permissions…" className="pl-9" data-testid="permission-filter" />
                  </div>
                </div>

                {active.key === "administrator" && (
                  <div className="mb-4 flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm">
                    <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div>Administrator retains the <code className="font-mono">*:*</code> wildcard by design. Edits here are advisory — the wildcard cannot be revoked.</div>
                  </div>
                )}

                <div className="space-y-6">
                  {DIMENSIONS.map(dim => {
                    const items = groupedCatalog[dim.key] || [];
                    if (items.length === 0) return null;
                    return (
                      <div key={dim.key}>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-heading font-semibold">{dim.label}</h3>
                          <span className="text-xs text-muted-foreground">· {dim.help}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {items.map(p => {
                            const granted = pending?.has(p.key) || hasPerm({ permissions: Array.from(pending || []) }, p.key);
                            const isChecked = pending?.has(p.key);
                            return (
                              <label
                                key={p.key}
                                data-testid={`perm-row-${p.key}`}
                                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                  granted ? "border-success/40 bg-success/5" : "border-border hover:bg-muted/40"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!isChecked}
                                  onChange={() => toggle(p.key)}
                                  data-testid={`perm-check-${p.key}`}
                                  className="mt-0.5"
                                />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{p.label}</div>
                                  <div className="text-[10px] font-mono text-muted-foreground truncate">{p.key}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
